<template>
  <div class="">
    <div class="flex items-center shrink-0" :class="auth.user ? 'gap-2' : 'gap-4'">
      <!-- Show LanguageSwitcher only when user is NOT logged in -->
      <ThemeSwitcher v-if="!auth.user" />
      <LanguageSwitcher v-if="!auth.user" />
      <div
        v-if="auth.user"
        @click="notificationEventToggle"
        class="relative flex items-center justify-center cursor-pointer w-9 h-9"
      >
        <div class="flex items-center justify-center w-5 h-5">
          <Icon :width="20" :height="20" type="notifications" class="flex items-center" />
        </div>
        <!-- no notifications -->
        <div
          v-if="notificationCount > 0"
          data-testid="notification-badge"
          :title="String(notificationCount)"
          class="absolute -right-0.5 -top-0.5 inline-flex items-center justify-center w-5 h-5 text-white font-bold bg-system dark:bg-system rounded-full overflow-hidden text-micro"
        >
          {{ formatBadgeCount(notificationCount) }}
        </div>
      </div>
      <!-- Chat: -->
      <Link
        v-if="auth.user"
        :href="route('chat.index')"
        class="relative flex items-center justify-center cursor-pointer w-9 h-9"
      >
        <div class="flex items-center justify-center w-5 h-5">
          <Icon type="chat" class="flex items-center" :width="20" :height="20" />
        </div>
        <div
          v-if="unreadMessagesCount > 0"
          data-testid="messages-badge"
          :title="String(unreadMessagesCount)"
          class="absolute -right-0.5 -top-0.5 inline-flex items-center justify-center w-5 h-5 text-white font-bold bg-system dark:bg-system rounded-full overflow-hidden text-micro"
        >
          {{ formatBadgeCount(unreadMessagesCount) }}
        </div>
      </Link>

      <Link
        v-if="!auth.user"
        :href="route('welcome')"
        class="text-xs font-semibold text-theme-tertiary-dark lg:text-sm"
      >
        {{ $t('Register') }}
      </Link>
      <Link
        v-if="!auth.user"
        :href="route('login')"
        class="text-xs font-semibold text-theme-tertiary-dark lg:text-sm"
      >
        {{ $t('Login') }}
      </Link>
    </div>
    <NotificationSidebar
      v-if="auth.user"
      :openEvent="openEvent"
      @updateNotificationClose="notificationEventToggle"
      @notifications-updated="fetchNotificationCount"
    />
  </div>
</template>
<script setup>
import Icon from '@/Icons/OptimizedIcons.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { defineAsyncComponent } from 'vue';
import { hasPermissions } from '@/Utils/permissions.js';
import { formatBadgeCount } from '@/Utils/badgeCount.js';
import { Link, usePage, router } from '@inertiajs/vue3';
import { default as LanguageSwitcher } from './LanguageSwitcher.vue';
import ThemeSwitcher from '@/Components/ThemeSwitcher.vue';
import { USER_PERMISSIONS } from '@/constants.js';
import axios from 'axios';

// Props
const props = defineProps({
  nav: String,
});

// Dynamic import for NotificationSidebar need only for auth users
const NotificationSidebar = defineAsyncComponent(() =>
  import('@/Layouts/Partials/NotificationSidebar.vue')
);

const page = usePage();
const auth = computed(() => page.props.auth);
const openEvent = ref(false);
const unreadMessagesCount = ref(0);
const notificationCount = ref(0);

// Computed properties
const showChat = computed(() => {
  return hasPermissions(USER_PERMISSIONS.HR_PORTAL) || props.nav === 'hr';
});

// Methods
const notificationEventToggle = () => {
  fetchNotificationCount();
  openEvent.value = !openEvent.value;
};

const fetchNotificationCount = async () => {
  try {
    const response = await axios.get(route('notifications.count'));
    notificationCount.value = response.data.count;
  } catch (error) {
    console.error('Error fetching notifications:', error);
  }
};

// Methods
const fetchUnreadMessagesCount = async () => {
  try {
    const response = await axios.get(route('chat.unread-messages'));
    unreadMessagesCount.value = response.data.unreadMessagesCount ?? 0;
  } catch (error) {
    // Error fetching unread messages
  }
};

// Lifecycle hooks
let removeNavigateListener = null;

onMounted(() => {
  if (auth.value.user) {
    fetchUnreadMessagesCount();
    fetchNotificationCount();

    // Refetch unread counts on every Inertia navigation
    removeNavigateListener = router.on('navigate', () => {
      fetchUnreadMessagesCount();
      fetchNotificationCount();
    });
  }
});

onUnmounted(() => {
  if (removeNavigateListener) {
    removeNavigateListener();
  }
});
</script>
